import { useState, useEffect } from 'react';
import { Plus, Search, Trash2, Edit2, Loader2 } from 'lucide-react';
import { api } from '@/core/apiClient';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface Company {
  id: number;
  name: string;
  tax_id: string;
  address?: string;
  logo?: string;
  is_active: boolean;
  created_at: string;
}

interface FormData {
  name: string;
  tax_id: string;
  address: string;
  logo: string;
  is_active: boolean;
}

const initialFormData: FormData = {
  name: '',
  tax_id: '',
  address: '',
  logo: '',
  is_active: true,
};

export default function AdminCompanies() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get<{ success: boolean; data: Company[] }>('/admin/companies');
      setCompanies(response.data || []);
    } catch (err) {
      console.error('[AdminCompanies] Error loading companies:', err);
      setError('Error en carregar les empreses');
    } finally {
      setLoading(false);
    }
  };

  const filteredCompanies = companies.filter(
    (c) =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.tax_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDelete = async (companyId: number) => {
    if (!window.confirm('Estàs segur que vols eliminar aquesta empresa?')) return;

    try {
      const response = await api.delete<{ success: boolean }>(`/admin/companies/${companyId}`);
      if (response.success) {
        setCompanies(companies.filter((c) => c.id !== companyId));
      }
    } catch (err) {
      console.error('[AdminCompanies] Error deleting company:', err);
      setError("Error en eliminar l'empresa");
    }
  };

  const openCreateModal = () => {
    setEditingId(null);
    setFormData(initialFormData);
    setFormError(null);
    setShowModal(true);
  };

  const openEditModal = (c: Company) => {
    setEditingId(c.id);
    setFormData({
      name: c.name,
      tax_id: c.tax_id,
      address: c.address || '',
      logo: c.logo || '',
      is_active: c.is_active,
    });
    setFormError(null);
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setFormError(null);

    try {
      const payload = {
        name: formData.name,
        tax_id: formData.tax_id,
        address: formData.address || undefined,
        logo: formData.logo || undefined,
        is_active: formData.is_active,
      };

      if (editingId) {
        await api.put(`/admin/companies/${editingId}`, payload);
      } else {
        await api.post('/admin/companies', payload);
      }
      setShowModal(false);
      await loadCompanies();
    } catch (err: any) {
      console.error('[AdminCompanies] Error saving company:', err);
      setFormError(err.response?.data?.message || "Error en desar l'empresa");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Empreses</h1>
          <p className="text-muted-foreground mt-1">Gestió d'empreses registrades</p>
        </div>
        <button
          onClick={openCreateModal}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 font-medium"
        >
          <Plus className="w-4 h-4" />
          Nova empresa
        </button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Cercar per nom, CIF..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      {error && (
        <div className="p-4 bg-red-500/10 text-red-500 rounded-lg border border-red-500/20">
          {error}
        </div>
      )}

      <div className="rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-foreground">Nom</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-foreground">CIF</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-foreground">Adreça</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-foreground">Estat</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-foreground">Accions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-muted-foreground">
                    Carregant empreses...
                  </td>
                </tr>
              ) : filteredCompanies.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-muted-foreground">
                    No hi ha empreses per mostrar
                  </td>
                </tr>
              ) : (
                filteredCompanies.map((c) => (
                  <tr key={c.id} className="border-b border-border hover:bg-muted/50">
                    <td className="px-6 py-4 text-xs font-medium text-foreground">{c.name}</td>
                    <td className="px-6 py-4 text-xs text-muted-foreground">{c.tax_id || '-'}</td>
                    <td className="px-6 py-4 text-xs text-muted-foreground">{c.address || '-'}</td>
                    <td className="px-6 py-4 text-xs">
                      <span
                        className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                          c.is_active ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-500'
                        }`}
                      >
                        {c.is_active ? 'Activa' : 'Inactiva'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEditModal(c)}
                          className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(c.id)}
                          className="p-2 text-muted-foreground hover:text-red-500 hover:bg-red-500/10 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-xs text-muted-foreground">Total: {filteredCompanies.length} empreses</div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar empresa' : 'Nova empresa'}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {formError && (
              <div className="p-3 bg-red-500/10 text-red-500 rounded-lg border border-red-500/20 text-xs">
                {formError}
              </div>
            )}

            <div>
              <label className="block text-xs font-medium mb-1">Nom</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">CIF / NIF</label>
              <input
                type="text"
                required
                value={formData.tax_id}
                onChange={(e) => setFormData((p) => ({ ...p, tax_id: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Adreça</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData((p) => ({ ...p, address: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Logo (URL)</label>
              <input
                type="text"
                value={formData.logo}
                onChange={(e) => setFormData((p) => ({ ...p, logo: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData((p) => ({ ...p, is_active: e.target.checked }))}
              />
              Empresa activa
            </label>

            <DialogFooter>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                disabled={saving}
                className="px-4 py-2 border rounded-lg hover:bg-muted transition-colors"
              >
                Cancel·lar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-60"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {saving ? 'Desant...' : 'Desar'}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
